/**
 * Thin wrapper around the BFF. The SPA only ever talks to /api/* on its own origin —
 * never to the MCP directly. Sessions ride on the httpOnly cookie, so every call
 * includes credentials.
 */

/** A non-2xx response. `status` lets callers branch (401 -> signed out); `message` is
 *  the BFF's error_description when present, ready to show the user; `code` is the BFF's
 *  machine error (e.g. 'invalid_csrf_token' vs 'write_not_allowed' — both 403). */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message || `Request failed (${status})`);
    this.name = 'ApiError';
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  // Destructure headers out and apply them LAST: a caller that passes its own headers
  // (e.g. mcpWrite's x-csrf-token) must not clobber Content-Type. Spreading `...init`
  // after the headers literal previously did exactly that, dropping Content-Type so the
  // BFF never JSON-parsed the body — every write arrived with an empty tool name.
  const { headers, ...rest } = init ?? {};
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    ...rest,
    headers: { 'Content-Type': 'application/json', ...((headers as Record<string, string>) ?? {}) },
  });
  if (!res.ok) {
    const raw = await res.text().catch(() => '');
    let message = raw;
    let code: string | undefined;
    try {
      const body = JSON.parse(raw) as { error_description?: string; error?: string };
      message = body.error_description ?? body.error ?? raw;
      code = body.error;
    } catch {
      /* non-JSON body — keep raw text */
    }
    throw new ApiError(res.status, message, code);
  }
  return res.json() as Promise<T>;
}
